import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useQuery } from '@axaou/ui';
import { useRecoilState, useRecoilValue } from 'recoil';
import styled from 'styled-components';

import { GeneBurdenManhattan } from '../Manhattan/GeneBurdenManhattan';
import { GeneBurdenComposite } from './GeneBurdenComposite';
import { GeneBurdenOverlay } from './GeneBurdenOverlay';
import { PrecomputedQQPlot } from '../VariantResults/PrecomputedQQPlot';
import type { QQPoint } from '../VariantResults/PrecomputedQQPlot';
import { axaouDevUrl, pouchDbName, cacheEnabled } from '../Query';
import { ancestryGroupAtom, burdenSetAtom, showQQOverlayAtom, geneBurdenViewModeAtom, geneBurdenShowSigAtom, locusMafAtom } from '../sharedState';
import { useAppNavigation } from '../hooks/useAppNavigation';
import { useRestoreFromUrl } from '../initialUrlState';
import {
  formatBurdenDirection,
  geneMacColumnMode,
  hasGeneEffectEstimate,
  type BurdenDirection,
} from '../geneAssociationSemantics';
import ExportDataButton from '../ExportDataButton';
import {
  formatMacCount,
  macCountForCsv,
  MAC_CASES_TOOLTIP,
  MAC_CONTROLS_TOOLTIP,
  MAC_TOTAL_TOOLTIP,
} from './Utils';
import { BurdenDirectionIndicator } from '../BurdenDirectionIndicator';

const Container = styled.div`
  width: 100%;
`;

const ToggleGroup = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 16px;
`;

const ToggleButton = styled.button<{ $active: boolean }>`
  padding: 8px 16px;
  font-size: 13px;
  font-family: GothamBook, sans-serif;
  background-color: ${({ $active }) => ($active ? '#262262' : 'var(--theme-surface-alt, #f5f5f5)')};
  color: ${({ $active }) => ($active ? 'white' : 'var(--theme-text, #333)')};
  border: 1px solid ${({ $active }) => ($active ? '#262262' : 'var(--theme-border, #ddd)')};
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background-color: ${({ $active }) => ($active ? '#262262' : 'var(--theme-border, #e8e8e8)')};
  }
`;

const ControlPanel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: var(--theme-surface-alt, #f5f5f5);
  border-radius: 4px;
  font-size: 12px;
  margin-bottom: 8px;
`;

const ControlGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const GeneSearchInput = styled.input`
  width: 180px;
  padding: 5px 8px;
  font-size: 12px;
  font-family: GothamBook, sans-serif;
  color: var(--theme-text, #333);
  background: var(--theme-surface, #fff);
  border: 1px solid var(--theme-border, #ccc);
  border-radius: 3px;

  &::placeholder {
    color: var(--theme-text-muted, #777);
  }
`;

const SmallButton = styled.button`
  font-size: 11px;
  padding: 4px 10px;
  cursor: pointer;
  background: var(--theme-surface, #fff);
  color: var(--theme-text, #333);
  border: 1px solid var(--theme-border, #ccc);
  border-radius: 3px;

  &:hover {
    background: var(--theme-surface-alt, #f0f0f0);
  }
`;

const TableContainer = styled.div`
  margin-top: 8px;
  border: 1px solid var(--theme-border, #e0e0e0);
  border-radius: 4px;
  max-height: 500px;
  overflow: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  color: var(--theme-text, #333);

  th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: var(--theme-surface-alt, #f5f5f5);
    padding: 10px 12px;
    text-align: left;
    font-weight: 600;
    border-bottom: 1px solid var(--theme-border, #e0e0e0);
    cursor: pointer;
    user-select: none;

    &:hover {
      background: var(--theme-border, #eaeaea);
    }

    &:first-child {
      cursor: default;
      width: 40px;
      text-align: center;

      &:hover {
        background: var(--theme-surface-alt, #f5f5f5);
      }
    }
  }

  td {
    padding: 8px 12px;
    border-bottom: 1px solid var(--theme-border, #f0f0f0);
  }

  tbody tr {
    transition: background-color 0.1s ease;
    cursor: pointer;

    &:hover {
      background: var(--theme-surface-alt, #f8f8f8);
    }
  }

  tbody tr.significant {
    background: rgba(76, 175, 80, 0.08);

    &:hover {
      background: rgba(76, 175, 80, 0.15);
    }
  }

  tbody tr.selected {
    background: rgba(25, 118, 210, 0.08);

    &:hover {
      background: rgba(25, 118, 210, 0.12);
    }
  }
`;

const SortIcon = styled.span<{ $active: boolean; $desc: boolean }>`
  margin-left: 4px;
  opacity: ${({ $active }) => ($active ? 1 : 0.3)};
  display: inline-block;
  transform: ${({ $desc }) => ($desc ? 'rotate(180deg)' : 'rotate(0deg)')};
`;

const SortHeaderButton = styled.button`
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font: inherit;
  font-weight: inherit;

  &:focus-visible {
    outline: 2px solid var(--theme-primary, #262262);
    outline-offset: 2px;
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: var(--theme-surface-alt, #f9f9f9);
  border-top: 1px solid var(--theme-border, #e0e0e0);
  font-size: 13px;
  color: var(--theme-text, #333);
`;

const PageButton = styled.button`
  padding: 6px 12px;
  font-size: 12px;
  background: var(--theme-surface, #fff);
  color: var(--theme-text, #333);
  border: 1px solid var(--theme-border, #ddd);
  border-radius: 4px;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background: var(--theme-surface-alt, #f0f0f0);
  }
`;

interface GeneAssociationResult {
  gene_id: string;
  gene_symbol: string;
  ancestry_group: string;
  contig: string;
  gene_start_position: number;
  pvalue: number | null;
  pvalue_burden: number | null;
  pvalue_skat: number | null;
  beta_burden: number | null;
  burden_direction: BurdenDirection | null;
  mac: number | null;
  mac_case?: number | null;
  mac_control?: number | null;
}

type SortKey = 'gene_symbol' | 'pvalue' | 'pvalue_burden' | 'pvalue_skat' | 'beta_burden' | 'mac' | 'mac_case' | 'mac_control';

const ANNOTATIONS = [
  { key: 'pLoF', label: 'pLoF' },
  { key: 'missenseLC', label: 'Missense' },
  { key: 'synonymous', label: 'Synonymous' },
] as const;

const PAGE_SIZE = 50;
const SIG_THRESHOLD = 2.5e-6;

interface Props {
  analysisId: string;
  traitType?: string | null;
}

type ViewMode = 'standard' | 'overlay' | 'heatmap' | 'qqplot';
type TestType = 'max' | 'skato' | 'burden' | 'skat';

const TEST_OPTIONS: Array<{ key: TestType; label: string }> = [
  { key: 'max', label: 'Best P' },
  { key: 'skato', label: 'SKAT-O' },
  { key: 'burden', label: 'Burden' },
  { key: 'skat', label: 'SKAT' },
];

const VALID_VIEW_MODES = new Set<string>(['standard', 'overlay', 'heatmap', 'qqplot'])

export const PhenotypeGeneBurdenTab: React.FC<Props> = ({ analysisId, traitType }) => {
  const ancestryGroup = useRecoilValue(ancestryGroupAtom);
  const showBeta = hasGeneEffectEstimate(ancestryGroup);
  const showDirection = !showBeta;
  const macColumnMode = geneMacColumnMode(traitType);
  const tableColumnCount = 6 + (macColumnMode === 'case-control' ? 2 : macColumnMode === 'total' ? 1 : 0);
  const [burdenSet, setBurdenSet] = useRecoilState(burdenSetAtom);
  const { goToGene } = useAppNavigation();

  const [viewMode, setViewMode] = useRecoilState<any>(geneBurdenViewModeAtom);
  useRestoreFromUrl(geneBurdenViewModeAtom, 'geneBurdenViewMode', VALID_VIEW_MODES);
  const [sortKey, setSortKey] = useState<SortKey>('pvalue');
  const [sortDesc, setSortDesc] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [geneFilter, setGeneFilter] = useState('');
  const [showOnlySignificant, setShowOnlySignificant] = useRecoilState(geneBurdenShowSigAtom);

  // Max MAF State
  const [maxMaf, setMaxMaf] = useRecoilState<any>(locusMafAtom);

  // Test type state for overlay view
  const [testType, setTestType] = useState<TestType>('max');

  // Label selection state
  const [selectedGeneIds, setSelectedGeneIds] = useState<Set<string>>(new Set());
  const [customLabelMode, setCustomLabelMode] = useState(false);

  interface Data {
    geneData: GeneAssociationResult[] | null;
  }

  const MAF_OPTIONS = [
    { value: 0.01, label: '1%' },
    { value: 0.001, label: '0.1%' },
    { value: 0.0001, label: '0.01%' },
  ];

  const { queryStates, anyLoading } = useQuery<Data>({
    dbName: pouchDbName,
    queries: [
      {
        url: `${axaouDevUrl}/phenotype/${analysisId}/genes?ancestry=${ancestryGroup}&annotation=${burdenSet}&max_maf=${maxMaf}&limit=50000&gene_contract=burden_direction_v1`,
        name: 'geneData',
      },
    ],
    deps: [analysisId, ancestryGroup, burdenSet, maxMaf],
    cacheEnabled,
  });

  // External navigation and shared controls can replace the table's data while
  // preserving this component. Never carry a now-invalid page into the new context.
  useEffect(() => {
    setCurrentPage(1);
  }, [analysisId, ancestryGroup, burdenSet, maxMaf]);

  // Sort and filter data
  const { sortedData, significantCount, totalPages } = useMemo(() => {
    const data = queryStates.geneData?.data ?? [];

    // Count significant genes
    const sigCount = data.filter((g) => g.pvalue != null && g.pvalue < SIG_THRESHOLD).length;

    // Filter by significance and by gene symbol/ID.
    const significanceFiltered = showOnlySignificant
      ? data.filter((g) => g.pvalue != null && g.pvalue < SIG_THRESHOLD)
      : data;
    const normalizedGeneFilter = geneFilter.trim().toLowerCase();
    const filtered = normalizedGeneFilter
      ? significanceFiltered.filter((g) =>
          (g.gene_symbol || '').toLowerCase().includes(normalizedGeneFilter) ||
          (g.gene_id || '').toLowerCase().includes(normalizedGeneFilter)
        )
      : significanceFiltered;

    const sorted = [...filtered].sort((a, b) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];

      if (aVal === null || aVal === undefined) {
        return bVal === null || bVal === undefined ? 0 : 1;
      }
      if (bVal === null || bVal === undefined) return -1;

      if (sortKey === 'gene_symbol') {
        const aLabel = a.gene_symbol || a.gene_id;
        const bLabel = b.gene_symbol || b.gene_id;
        return sortDesc ? bLabel.localeCompare(aLabel) : aLabel.localeCompare(bLabel);
      }

      return sortDesc ? (bVal as number) - (aVal as number) : (aVal as number) - (bVal as number);
    });

    return {
      sortedData: sorted,
      significantCount: sigCount,
      totalPages: Math.ceil(sorted.length / PAGE_SIZE),
    };
  }, [queryStates.geneData?.data, sortKey, sortDesc, showOnlySignificant, geneFilter]);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return sortedData.slice(start, start + PAGE_SIZE);
  }, [sortedData, currentPage]);

  // Label selection callbacks
  const toggleGene = useCallback((geneId: string) => {
    setCustomLabelMode(true);
    setSelectedGeneIds((prev) => {
      const next = new Set(prev);
      if (next.has(geneId)) {
        next.delete(geneId);
      } else {
        next.add(geneId);
      }
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedGeneIds(new Set());
    setCustomLabelMode(true);
  }, []);

  const resetToDefault = useCallback(() => {
    setSelectedGeneIds(new Set());
    setCustomLabelMode(false);
  }, []);

  const selectAllFiltered = useCallback(() => {
    setCustomLabelMode(true);
    const ids = new Set(sortedData.map((g) => g.gene_id));
    setSelectedGeneIds(ids);
  }, [sortedData]);

  const selectSignificant = useCallback(() => {
    setCustomLabelMode(true);
    const allData = queryStates.geneData?.data ?? [];
    const ids = new Set(
      allData.filter((g) => g.pvalue != null && g.pvalue < SIG_THRESHOLD).map((g) => g.gene_id)
    );
    setSelectedGeneIds(ids);
  }, [queryStates.geneData?.data]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDesc(!sortDesc);
    } else {
      setSortKey(key);
      setSortDesc(false);
    }
    setCurrentPage(1);
  };

  const getAriaSort = (key: SortKey): 'ascending' | 'descending' | 'none' => {
    if (sortKey !== key) return 'none';
    return sortDesc ? 'descending' : 'ascending';
  };

  const handleGeneClick = (gene: GeneAssociationResult) => {
    goToGene(gene.gene_id, { fromPhenotype: true });
  };

  const handleGeneClickFromPlot = (geneId: string) => {
    goToGene(geneId, { fromPhenotype: true });
  };

  const handleAnnotationChange = (annotation: typeof burdenSet) => {
    setBurdenSet(annotation);
    setCurrentPage(1);
    // Reset selection when changing annotation
    setSelectedGeneIds(new Set());
    setCustomLabelMode(false);
  };

  const formatPvalue = (p: number | null) => {
    if (p === null) return '—';
    return p.toExponential(2);
  };

  const formatBeta = (b: number | null) => {
    if (b === null) return '—';
    return b.toFixed(3);
  };

  // Determine which genes have labels (for checkbox state)
  const labeledGeneIds = useMemo(() => {
    if (customLabelMode) {
      return selectedGeneIds;
    }
    // Default: top 10 by p-value
    const allData = queryStates.geneData?.data ?? [];
    const top10 = [...allData]
      .filter((g) => g.pvalue != null)
      .sort((a, b) => (a.pvalue ?? Infinity) - (b.pvalue ?? Infinity))
      .slice(0, 10)
      .map((g) => g.gene_id);
    return new Set(top10);
  }, [customLabelMode, selectedGeneIds, queryStates.geneData?.data]);

  const geneData = queryStates.geneData?.data ?? [];
  const [showQQOverlay, setShowQQOverlay] = useRecoilState(showQQOverlayAtom);

  // Compute mini QQ plot points from gene burden p-values
  const qqPoints = useMemo(() => {
    if (!showQQOverlay) return null;
    const validGenes = geneData
      .filter((g): g is GeneAssociationResult & { pvalue: number } =>
        g.pvalue != null && g.pvalue > 0
      )
      .sort((a, b) => a.pvalue - b.pvalue);
    if (validGenes.length === 0) return null;
    return validGenes.map((g, i): QQPoint => ({
      x: -Math.log10((i + 1) / (validGenes.length + 1)),
      y: -Math.log10(g.pvalue),
    }));
  }, [geneData, showQQOverlay]);

  const qqMiniInset = useMemo(() => {
    if (!qqPoints) return undefined;
    return (w: number, h: number) => (
      <PrecomputedQQPlot points={qqPoints} width={w} height={h} />
    );
  }, [qqPoints]);

  const exportColumns = useMemo(() => [
    { key: 'gene_id', heading: 'Gene ID' },
    { key: 'gene_symbol', heading: 'Gene' },
    { key: 'contig', heading: 'Chromosome' },
    { key: 'gene_start_position', heading: 'Position' },
    { key: 'pvalue', heading: 'P SKAT-O' },
    { key: 'pvalue_burden', heading: 'P Burden' },
    { key: 'pvalue_skat', heading: 'P SKAT' },
    ...(showBeta
      ? [{ key: 'beta_burden', heading: 'Beta' }]
      : [{
          key: 'burden_direction',
          heading: 'Burden direction',
          renderForCSV: (row: GeneAssociationResult) =>
            formatBurdenDirection(row.burden_direction),
        }]),
    ...(macColumnMode === 'case-control' ? [
      {
        key: 'mac_case',
        heading: 'MAC cases',
        renderForCSV: (row: GeneAssociationResult) => macCountForCsv(row.mac_case),
      },
      {
        key: 'mac_control',
        heading: 'MAC controls',
        renderForCSV: (row: GeneAssociationResult) => macCountForCsv(row.mac_control),
      },
    ] : macColumnMode === 'total' ? [{
      key: 'mac',
      heading: 'MAC total',
      renderForCSV: (row: GeneAssociationResult) => macCountForCsv(row.mac),
    }] : []),
  ], [showBeta, macColumnMode]);

  return (
    <Container>
      {/* View Mode Toggle */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <ToggleGroup>
          <ToggleButton
            $active={viewMode === 'standard'}
            onClick={() => setViewMode('standard')}
          >
            Single Annotation
          </ToggleButton>
          {/* TODO: Fix overlay plot responsiveness - see plans/v8-near-final/51-overlay-manhattan.md
          <ToggleButton
            $active={viewMode === 'overlay'}
            onClick={() => setViewMode('overlay')}
          >
            Overlay Plot
          </ToggleButton>
          */}
          <ToggleButton
            $active={viewMode === 'heatmap'}
            onClick={() => setViewMode('heatmap')}
          >
            Heatmap
          </ToggleButton>
          <ToggleButton
            $active={viewMode === 'qqplot'}
            onClick={() => setViewMode('qqplot')}
          >
            QQ Plot
          </ToggleButton>
          {viewMode === 'standard' && (
            <ToggleButton
              $active={showQQOverlay}
              onClick={() => setShowQQOverlay(!showQQOverlay)}
              title={showQQOverlay ? 'Hide QQ overlay' : 'Show QQ overlay'}
            >
              QQ Overlay
            </ToggleButton>
          )}
        </ToggleGroup>
      </div>

      {/* TODO: Fix overlay plot responsiveness - see plans/v8-near-final/51-overlay-manhattan.md
      {viewMode === 'overlay' && (
        <>
          <div style={{ display: 'flex', gap: '16px', marginBottom: 8 }}>
            <ToggleGroup>
              {MAF_OPTIONS.map((maf) => (
                <ToggleButton
                  key={maf.value}
                  $active={maxMaf === maf.value}
                  onClick={() => setMaxMaf(maf.value)}
                >
                  MAF ≤ {maf.label}
                </ToggleButton>
              ))}
            </ToggleGroup>
            <ToggleGroup>
              {TEST_OPTIONS.map((opt) => (
                <ToggleButton
                  key={opt.key}
                  $active={testType === opt.key}
                  onClick={() => setTestType(opt.key)}
                >
                  {opt.label}
                </ToggleButton>
              ))}
            </ToggleGroup>
          </div>
          <GeneBurdenOverlay analysisId={analysisId} maxMaf={maxMaf} testType={testType} />
        </>
      )}
      */}

      {/* QQ Plot View */}
      {viewMode === 'qqplot' && (
        (() => {
          const validGenes = (queryStates.geneData?.data ?? [])
            .filter((g): g is GeneAssociationResult & { pvalue: number } =>
              g.pvalue != null && g.pvalue > 0
            )
            .sort((a, b) => a.pvalue - b.pvalue);

          const qqPoints: QQPoint[] = validGenes.map((g, i) => ({
            x: -Math.log10((i + 1) / (validGenes.length + 1)),
            y: -Math.log10(g.pvalue),
            label: g.gene_symbol,
          }));

          return (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <PrecomputedQQPlot
                points={qqPoints}
                width={700}
                height={500}
              />
            </div>
          );
        })()
      )}

      {/* Heatmap View */}
      {viewMode === 'heatmap' && (
        <GeneBurdenComposite analysisId={analysisId} macColumnMode={macColumnMode} maxMaf={maxMaf} />
      )}

      {/* Standard View */}
      {viewMode === 'standard' && (
        <>
          {/* Annotation and MAF Toggles */}
          <div style={{ display: 'flex', gap: '16px' }}>
            <ToggleGroup>
              {ANNOTATIONS.map((ann) => (
                <ToggleButton
                  key={ann.key}
                  $active={burdenSet === ann.key}
                  onClick={() => handleAnnotationChange(ann.key as typeof burdenSet)}
                >
                  {ann.label}
                </ToggleButton>
              ))}
            </ToggleGroup>

            <ToggleGroup>
              {MAF_OPTIONS.map((maf) => (
                <ToggleButton
                  key={maf.value}
                  $active={maxMaf === maf.value}
                  onClick={() => {
                    setMaxMaf(maf.value);
                    setCurrentPage(1);
                    setSelectedGeneIds(new Set());
                    setCustomLabelMode(false);
                  }}
                >
                  MAF ≤ {maf.label}
                </ToggleButton>
              ))}
            </ToggleGroup>
          </div>

      {/* Manhattan Plot with Labels */}
      <GeneBurdenManhattan
        analysisId={analysisId}
        geneData={geneData}
        selectedGeneIds={selectedGeneIds}
        customLabelMode={customLabelMode}
        onGeneClick={handleGeneClickFromPlot}
        macColumnMode={macColumnMode}
        inset={qqMiniInset}
      />

      {/* Control Panel */}
      <ControlPanel>
        <ControlGroup>
          <GeneSearchInput
            type="search"
            value={geneFilter}
            onChange={(event) => {
              setGeneFilter(event.target.value);
              setCurrentPage(1);
            }}
            placeholder="Search table by gene"
            aria-label="Search phenotype results table by gene symbol or ID"
          />
          <span style={{ color: 'var(--theme-text, #333)' }}>
            <strong>{sortedData.length.toLocaleString()}</strong>
            {showOnlySignificant ? ` / ${(queryStates.geneData?.data ?? []).length.toLocaleString()}` : ''} genes
          </span>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={showOnlySignificant}
              onChange={(e) => {
                setShowOnlySignificant(e.target.checked);
                setCurrentPage(1);
              }}
              style={{ cursor: 'pointer' }}
            />
            <span style={{ fontSize: 11 }}>Significant only ({significantCount})</span>
          </label>
          {customLabelMode ? (
            <span style={{ color: 'var(--theme-primary, #262262)' }}>
              <strong>{selectedGeneIds.size}</strong> labeled
            </span>
          ) : (
            <span style={{ color: 'var(--theme-text-muted, #666)', fontSize: 11 }}>Top 10 labeled</span>
          )}
        </ControlGroup>
        <ControlGroup>
          {!customLabelMode && (
            <>
              <SmallButton onClick={selectSignificant} title="Label all significant genes">
                Label significant ({significantCount})
              </SmallButton>
              <SmallButton onClick={selectAllFiltered} title="Label all filtered genes">
                Label all ({sortedData.length})
              </SmallButton>
            </>
          )}
          {customLabelMode && (
            <>
              {selectedGeneIds.size > 0 && (
                <SmallButton onClick={clearSelection}>Clear all</SmallButton>
              )}
              <SmallButton onClick={resetToDefault}>Reset to top 10</SmallButton>
            </>
          )}
        </ControlGroup>
      </ControlPanel>

      {/* Gene Results Table */}
      <TableContainer>
        <Table>
          <thead>
            <tr>
              <th>Label</th>
              <th onClick={() => handleSort('gene_symbol')}>
                Gene
                <SortIcon $active={sortKey === 'gene_symbol'} $desc={sortDesc}>▲</SortIcon>
              </th>
              <th onClick={() => handleSort('pvalue')}>
                P SKAT-O
                <SortIcon $active={sortKey === 'pvalue'} $desc={sortDesc}>▲</SortIcon>
              </th>
              <th onClick={() => handleSort('pvalue_burden')}>
                P Burden
                <SortIcon $active={sortKey === 'pvalue_burden'} $desc={sortDesc}>▲</SortIcon>
              </th>
              <th onClick={() => handleSort('pvalue_skat')}>
                P SKAT
                <SortIcon $active={sortKey === 'pvalue_skat'} $desc={sortDesc}>▲</SortIcon>
              </th>
              {showBeta && (
                <th onClick={() => handleSort('beta_burden')}>
                  Beta
                  <SortIcon $active={sortKey === 'beta_burden'} $desc={sortDesc}>▲</SortIcon>
                </th>
              )}
              {showDirection && (
                <th title="META burden-statistic direction; magnitude unavailable">Direction</th>
              )}
              {macColumnMode === 'case-control' && (
                <>
                  <th aria-sort={getAriaSort('mac_case')} title={MAC_CASES_TOOLTIP}>
                    <SortHeaderButton
                      type="button"
                      onClick={() => handleSort('mac_case')}
                      aria-label={`Sort by MAC cases; currently ${getAriaSort('mac_case')}`}
                    >
                      MAC cases
                      <SortIcon $active={sortKey === 'mac_case'} $desc={sortDesc}>▲</SortIcon>
                    </SortHeaderButton>
                  </th>
                  <th aria-sort={getAriaSort('mac_control')} title={MAC_CONTROLS_TOOLTIP}>
                    <SortHeaderButton
                      type="button"
                      onClick={() => handleSort('mac_control')}
                      aria-label={`Sort by MAC controls; currently ${getAriaSort('mac_control')}`}
                    >
                      MAC controls
                      <SortIcon $active={sortKey === 'mac_control'} $desc={sortDesc}>▲</SortIcon>
                    </SortHeaderButton>
                  </th>
                </>
              )}
              {macColumnMode === 'total' && (
                <th aria-sort={getAriaSort('mac')} title={MAC_TOTAL_TOOLTIP}>
                  <SortHeaderButton
                    type="button"
                    onClick={() => handleSort('mac')}
                    aria-label={`Sort by MAC total; currently ${getAriaSort('mac')}`}
                  >
                    MAC total
                    <SortIcon $active={sortKey === 'mac'} $desc={sortDesc}>▲</SortIcon>
                  </SortHeaderButton>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {anyLoading() ? (
              <tr>
                <td colSpan={tableColumnCount} style={{ textAlign: 'center', padding: 20 }}>
                  Loading...
                </td>
              </tr>
            ) : queryStates.geneData?.error ? (
              <tr>
                <td colSpan={tableColumnCount} style={{ textAlign: 'center', padding: 20, color: 'var(--theme-text-muted, #666)' }}>
                  Gene burden data not available for this phenotype
                </td>
              </tr>
            ) : paginatedData.length === 0 ? (
              <tr>
                <td colSpan={tableColumnCount} style={{ textAlign: 'center', padding: 20, color: 'var(--theme-text-muted, #666)' }}>
                  No gene burden results found
                </td>
              </tr>
            ) : (
              paginatedData.map((gene) => {
                const isSignificant = gene.pvalue !== null && gene.pvalue < SIG_THRESHOLD;
                const hasLabel = labeledGeneIds.has(gene.gene_id);
                const isSelected = selectedGeneIds.has(gene.gene_id);

                let className = '';
                if (isSignificant) className = 'significant';
                if (isSelected) className = 'selected';

                return (
                  <tr key={gene.gene_id} className={className}>
                    <td style={{ width: 40, textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={hasLabel}
                        onChange={() => toggleGene(gene.gene_id)}
                        onClick={(e) => e.stopPropagation()}
                        title={hasLabel ? 'Remove label' : 'Add label'}
                        style={{ cursor: 'pointer' }}
                      />
                    </td>
                    <td
                      style={{ fontWeight: 500, cursor: 'pointer' }}
                      onClick={() => handleGeneClick(gene)}
                    >
                      {gene.gene_symbol || gene.gene_id}
                    </td>
                    <td onClick={() => handleGeneClick(gene)}>{formatPvalue(gene.pvalue)}</td>
                    <td onClick={() => handleGeneClick(gene)}>{formatPvalue(gene.pvalue_burden)}</td>
                    <td onClick={() => handleGeneClick(gene)}>{formatPvalue(gene.pvalue_skat)}</td>
                    {showBeta && (
                      <td onClick={() => handleGeneClick(gene)}>{formatBeta(gene.beta_burden)}</td>
                    )}
                    {showDirection && (
                      <td onClick={() => handleGeneClick(gene)}>
                        <BurdenDirectionIndicator direction={gene.burden_direction} />
                      </td>
                    )}
                    {macColumnMode === 'case-control' && (
                      <>
                        <td onClick={() => handleGeneClick(gene)}>{formatMacCount(gene.mac_case)}</td>
                        <td onClick={() => handleGeneClick(gene)}>{formatMacCount(gene.mac_control)}</td>
                      </>
                    )}
                    {macColumnMode === 'total' && (
                      <td onClick={() => handleGeneClick(gene)}>{formatMacCount(gene.mac)}</td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
        {!anyLoading() && sortedData.length > PAGE_SIZE && (
          <Pagination>
            <span>
              Showing {(currentPage - 1) * PAGE_SIZE + 1}–
              {Math.min(currentPage * PAGE_SIZE, sortedData.length)} of{' '}
              {sortedData.length.toLocaleString()} genes
            </span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <PageButton
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                ← Previous
              </PageButton>
              <span>
                Page {currentPage} of {totalPages}
              </span>
              <PageButton
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next →
              </PageButton>
            </div>
          </Pagination>
        )}
      </TableContainer>
      <div style={{ marginTop: 12 }}>
        <ExportDataButton
          exportFileName={`phenotype-gene-burden_${analysisId}_${ancestryGroup}_${burdenSet}_maf-${maxMaf}`}
          data={sortedData}
          columns={exportColumns}
          enableExport
        />
      </div>
        </>
      )}
    </Container>
  );
};

export default PhenotypeGeneBurdenTab;
